import { useEffect, useState } from 'react';
import { Alert, ControlLabel, FormControl, FormGroup, HelpBlock, Modal } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { IOpoContact, IOpoContactPosition, IOpoContactRequest } from '@nkr/models/opo-contact';
import { IOpoSaveResult } from '@nkr/models/opo-document';
import { OpoModalButtons, OpoModalForm } from '../../opo-styles';
import ContactRoleCheckboxes from './contact-role-checkboxes';

const maxText =(label: string, max: number) => z.string().trim().max(max, `${label} cannot be longer than ${max} characters`);

const contactSchema = z.object({
    firstName: maxText('First name', 50).min(1, 'First name is required'),
    lastName: maxText('Last name', 50).min(1, 'Last name is required'),
    title: maxText('Title', 100),
    email: z.string().trim().min(1, 'Email is required').max(100, 'Email cannot be longer than 100 characters').email('Please enter a valid email address'),
    officePhone: maxText('Office phone', 24),
    mobilePhone: maxText('Mobile phone', 24),
    positions: z.array(z.number()).min(1, 'Select at least one role')
});

type ContactFormValues = z.infer<typeof contactSchema>;

const emptyValues: ContactFormValues = {
    firstName: '',
    lastName: '',
    title: '',
    email: '',
    officePhone: '',
    mobilePhone: '',
    positions: []
};

const toFormValues = (contact: IOpoContact): ContactFormValues => ({
    firstName: contact.firstName,
    lastName: contact.lastName,
    title: contact.title,
    email: contact.email,
    officePhone: contact.officePhone,
    mobilePhone: contact.mobilePhone,
    positions: contact.positions.map(x => x.id)
});

type TextField = Exclude<keyof ContactFormValues, 'positions'>;

interface IProps {
    show: boolean,
    contact: IOpoContact | null,
    positions: IOpoContactPosition[],
    onSave: (contactId: number | null, request: IOpoContactRequest) => Promise<IOpoSaveResult>,
    onCancel: () => void
}

const ContactModal = ({ show, contact, positions, onSave, onCancel }: IProps) => {
    const [serverError, setServerError] = useState('');
    const { control, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<ContactFormValues>({
        resolver: zodResolver(contactSchema),
        defaultValues: emptyValues
    });

    useEffect(() => {
        if (show) {
            reset(contact ? toFormValues(contact) : emptyValues);
            setServerError('');
        }
    }, [show, contact]);

    const onSubmit = async (values: ContactFormValues) => {
        setServerError('');

        const result = await onSave(contact ? contact.id : null, {
            firstName: values.firstName,
            lastName: values.lastName,
            title: values.title,
            email: values.email,
            officePhone: values.officePhone,
            mobilePhone: values.mobilePhone,
            positions: values.positions
        });

        if (!result.success) {
            setServerError(result.message);
        }
    };

    const renderGroup = (label: string, error: string | undefined, input: JSX.Element, className?: string, required = false) => (
        <FormGroup className={[className, error ? 'has-error' : null].filter(x => x).join(' ')}>
            <ControlLabel>{label}{required && <span className='required' aria-hidden='true'> *</span>}</ControlLabel>
            {input}
            {error && (
                <HelpBlock>{error}</HelpBlock>
            )}
        </FormGroup>
    );

    const renderText = (name: TextField, label: string, maxLength: number, className?: string, type = 'text', required = false) => (
        <Controller control={control} name={name} render={({ field }) =>
            renderGroup(label, errors[name]?.message, <FormControl type={type} maxLength={maxLength} required={required} aria-required={required} value={field.value} onChange={field.onChange} onBlur={field.onBlur} />, className, required)
        } />
    );

    return (
        <Modal show={show} onHide={onCancel} backdrop='static'>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <Modal.Header closeButton>
                    <Modal.Title>{contact ? 'Edit Contact' : 'Add Contact'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <OpoModalForm>
                        {serverError && (
                            <Alert bsStyle='danger'>{serverError}</Alert>
                        )}

                        <div className='form-grid'>
                            {renderText('firstName', 'First Name', 50, undefined, 'text', true)}
                            {renderText('lastName', 'Last Name', 50, undefined, 'text', true)}
                            {renderText('title', 'Title', 100, 'full-width')}
                            {renderText('email', 'Email', 100, 'full-width', 'email', true)}
                            {renderText('officePhone', 'Office Phone', 24, undefined, 'tel')}
                            {renderText('mobilePhone', 'Mobile Phone', 24, undefined, 'tel')}

                            <Controller control={control} name='positions' render={({ field: { value, onChange } }) =>
                                renderGroup('Roles', errors.positions?.message, (
                                    <ContactRoleCheckboxes positions={positions} value={value} onChange={onChange} />
                                ), 'full-width', true)
                            } />
                        </div>

                        <p style={{ margin: '14px 0 0', fontSize: 13, color: '#6B7A8F' }}>
                            <span className='required'>*</span> Required
                        </p>
                    </OpoModalForm>
                </Modal.Body>
                <Modal.Footer>
                    <OpoModalButtons>
                        <button type='submit' className='btn btn-primary' disabled={isSubmitting}>{contact ? 'Save Changes' : 'Add Contact'}</button>
                        <button type='button' className='btn' onClick={onCancel}>Cancel</button>
                    </OpoModalButtons>
                </Modal.Footer>
            </form>
        </Modal>
    );
}

export default ContactModal;
